import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import {
  colors,
  typography,
  borderRadius,
  getCosmicBackground,
  getCardStyle,
  getButtonStyle,
  getInputStyle,
  getLabelStyle,
} from '../theme';

interface BirthData {
  id: number;
  birth_date: string;
  birth_time: string;
  birth_location: string;
  latitude: number;
  longitude: number;
  timezone: string;
  is_locked: boolean;
}

const BirthDataPage: React.FC = () => {
  const navigate = useNavigate();
  const [existingData, setExistingData] = useState<BirthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    birth_date: '',
    birth_time: '',
    birth_location: '',
    timezone: ''
  });

  const token = localStorage.getItem('auth_token');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchBirthData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  const fetchBirthData = async () => {
    try {
      const response = await fetch(`${API_URL}/users/birth-data`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setExistingData(data);
      }
    } catch (error) {
      // No birth data yet
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/users/birth-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          birth_date: new Date(formData.birth_date).toISOString(),
          birth_time: formData.birth_time || '12:00',
          birth_location: formData.birth_location,
          latitude: 0,
          longitude: 0,
          timezone: formData.timezone
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to save birth data');
      }

      setSuccess('Birth data saved successfully! This data is now permanently locked.');
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        ...getCosmicBackground(),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: colors.text.starWhite,
        fontFamily: typography.fontFamily.heading,
        fontSize: typography.fontSize.xl,
        letterSpacing: typography.letterSpacing.wider,
      }}>
        <span style={{ color: colors.accent.gold, marginRight: '12px' }}>★</span>
        Loading...
      </div>
    );
  }

  return (
    <div style={{
      ...getCosmicBackground(),
      padding: '40px 20px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Star pattern background */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `
          radial-gradient(2px 2px at 20px 30px, rgba(212, 175, 55, 0.3), transparent),
          radial-gradient(1px 1px at 90px 40px, rgba(255, 255, 255, 0.4), transparent),
          radial-gradient(2px 2px at 130px 80px, rgba(157, 143, 219, 0.2), transparent)
        `,
        backgroundSize: '200px 200px',
        pointerEvents: 'none',
        opacity: 0.5,
        zIndex: 0,
      }} />

      {/* Header */}
      <div style={{ maxWidth: '600px', margin: '0 auto 30px', textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <Link to="/dashboard" style={{
          color: colors.text.secondary,
          textDecoration: 'none',
          fontSize: typography.fontSize.sm,
        }}>
          ← Back to Dashboard
        </Link>
      </div>

      <div style={{
        ...getCardStyle('default'),
        maxWidth: '600px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 1,
      }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            fontSize: '56px',
            marginBottom: '16px',
            color: colors.accent.gold,
            textShadow: `0 0 30px rgba(212, 175, 55, 0.5)`,
          }}>☼</div>
          <h1 style={{
            fontFamily: typography.fontFamily.heading,
            fontSize: typography.fontSize['3xl'],
            fontWeight: typography.fontWeight.bold,
            color: colors.text.starWhite,
            margin: '0 0 12px 0',
            letterSpacing: typography.letterSpacing.wide,
          }}>Birth Date</h1>
          <p style={{ color: colors.text.secondary, margin: 0, fontSize: typography.fontSize.base }}>
            {existingData
              ? 'Your birth data is permanently locked for accurate calculations.'
              : 'Enter your birth information for personalized astrological guidance.'}
          </p>
        </div>

        {error && (
          <div style={{
            background: colors.status.errorBg,
            color: colors.status.error,
            padding: '14px 18px',
            borderRadius: borderRadius.md,
            marginBottom: '24px',
            border: `1px solid rgba(248, 113, 113, 0.3)`,
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            background: colors.status.successBg,
            color: colors.status.success,
            padding: '14px 18px',
            borderRadius: borderRadius.md,
            marginBottom: '24px',
            border: `1px solid rgba(74, 222, 128, 0.3)`,
          }}>
            {success}
          </div>
        )}

        {existingData ? (
          <div>
            <div style={{
              background: 'rgba(74, 63, 138, 0.15)',
              borderRadius: borderRadius.md,
              padding: '24px',
              border: `1px solid ${colors.border.subtle}`,
            }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ ...getLabelStyle('dark'), color: colors.text.muted }}>Birth Date</label>
                <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.text.starWhite }}>
                  {new Date(existingData.birth_date).toLocaleDateString()}
                </p>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ ...getLabelStyle('dark'), color: colors.text.muted }}>Birth Time</label>
                <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.text.starWhite }}>{existingData.birth_time}</p>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ ...getLabelStyle('dark'), color: colors.text.muted }}>Birth Location</label>
                <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.text.starWhite }}>{existingData.birth_location}</p>
              </div>
              <div>
                <label style={{ ...getLabelStyle('dark'), color: colors.text.muted }}>Timezone</label>
                <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.text.starWhite }}>{existingData.timezone}</p>
              </div>
            </div>
            <div style={{
              marginTop: '24px',
              padding: '18px',
              background: 'rgba(212, 175, 55, 0.1)',
              borderRadius: borderRadius.md,
              textAlign: 'center',
              border: `1px solid ${colors.border.gold}`,
            }}>
              <span style={{ color: colors.accent.gold, fontSize: typography.fontSize.sm }}>
                {'\u{1F512}'} Birth data is permanently locked and cannot be changed.
              </span>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label style={getLabelStyle('dark')}>Birth Date *</label>
              <input
                type="date"
                value={formData.birth_date}
                onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                required
                style={getInputStyle('dark')}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={getLabelStyle('dark')}>Birth Time (HH:MM format)</label>
              <input
                type="time"
                value={formData.birth_time}
                onChange={(e) => setFormData({ ...formData, birth_time: e.target.value })}
                style={getInputStyle('dark')}
              />
              <p style={{ fontSize: typography.fontSize.xs, color: colors.text.muted, margin: '8px 0 0 0' }}>
                Optional - if known, provides more accurate readings.
              </p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={getLabelStyle('dark')}>Birth Location *</label>
              <input
                type="text"
                value={formData.birth_location}
                onChange={(e) => setFormData({ ...formData, birth_location: e.target.value })}
                required
                style={getInputStyle('dark')}
                placeholder="City, State/Province, Country"
              />
            </div>

            <div style={{ marginBottom: '28px' }}>
              <label style={getLabelStyle('dark')}>Timezone *</label>
              <select
                value={formData.timezone}
                onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                required
                style={getInputStyle('dark')}
              >
                <option value="">Select timezone...</option>
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="America/Phoenix">Arizona (No DST)</option>
                <option value="America/Anchorage">Alaska Time</option>
                <option value="Pacific/Honolulu">Hawaii Time</option>
                <option value="Europe/London">GMT/BST (London)</option>
                <option value="Europe/Paris">CET (Paris)</option>
                <option value="Asia/Tokyo">JST (Tokyo)</option>
                <option value="Asia/Shanghai">CST (Shanghai)</option>
                <option value="Australia/Sydney">AEST (Sydney)</option>
              </select>
            </div>

            <div style={{
              padding: '18px',
              background: 'rgba(251, 191, 36, 0.1)',
              borderRadius: borderRadius.md,
              marginBottom: '24px',
              textAlign: 'center',
              border: `1px solid ${colors.status.warning}`,
            }}>
              <span style={{ color: colors.status.warning, fontSize: typography.fontSize.sm }}>
                ⚠ Please double-check your information. Birth data cannot be changed after submission.
              </span>
            </div>

            <button
              type="submit"
              disabled={saving}
              style={{
                ...getButtonStyle('gold'),
                width: '100%',
                padding: '16px',
                opacity: saving ? 0.7 : 1,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Saving...' : '\u{1F512} Save Birth Date (Permanent)'}
            </button>
          </form>
        )}

        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <Link to="/dashboard" style={{
            color: colors.accent.gold,
            textDecoration: 'none',
            fontSize: typography.fontSize.sm,
          }}>
            ← Back to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BirthDataPage;
